/** @jsxImportSource @emotion/react */
import { useTheme } from "@emotion/react";
import { useEffect, useState } from "react";
import { useParams, Redirect } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import Navbar from "component/Navbar";
import Step1 from "./Step1";
import Step2 from "./Step2";
import Step3 from "./Step3";
import Step4 from "./Step4";
import FinalStep from "./FinalStep";
import { useUpdateOrigin, useGetOrigin } from "utils/hooks/origin.hook";
import { DrugOrigin, setFormData } from "utils/drugOrigin";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useGetPlants } from "utils/hooks/plant.hook";
import UnsavedChangeDialog from "component/UnsavedChangeDialog";

function ExistingDrugOrigin() {
  const theme = useTheme();
  const [redirectionPath, setRedirectionPath] = useState<string | null>(null);
  const [valuesChanged, setValuesChanged] = useState(false);
  const [step, setStep] = useState(1);
  const { plants } = useGetPlants();
  const params = useParams<{ id: string }>();
  const { mutate: update } = useUpdateOrigin(params.id);
  const { isLoading, isError, origin } = useGetOrigin(params.id);
  const methods = useForm<DrugOrigin>({
    mode: "onChange",
    defaultValues: {
      productionCountriesDrugFormulation: [
        {
          countryId: null,
          percentage: null,
        },
      ],
      productionCountriesPrimaryPackaging: [
        {
          countryId: null,
          percentage: null,
        },
      ],
    },
  });
  const { watch, setValue } = methods;

  const handleStep = (type: "decrement" | "increment") => {
    setStep((step) => (type === "decrement" ? step - 1 : step + 1));
  };

  useEffect(() => {
    if (origin) setFormData(origin, setValue);
  }, [origin, setValue]);

  const handleSave = () => {
    update(watch());
  };

  if (isLoading) {
    return (
      <div
        css={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  if (isError) {
    return <Redirect to="/drug_origin/new" />;
  }

  return (
    <>
      <UnsavedChangeDialog
        redirectionPath={redirectionPath}
        setRedirectionPath={setRedirectionPath}
        valuesChanged={valuesChanged}
        setValuesChanged={setValuesChanged}
        saveAction={handleSave}
        watch={watch}
      />
      <div
        css={{
          flexGrow: 1,
          backgroundColor: theme.white,
          color: theme.black,
          display: "flex",
        }}
      >
        <Navbar customRedirection={setRedirectionPath} />
        <FormProvider {...methods}>
          <form
            css={{
              flexGrow: 1,
              overflowY: "auto",
            }}
          >
            {step <= 1 && (
              <Step1
                handleSaveBeforeLeave={setRedirectionPath}
                handleStep={handleStep}
              />
            )}
            {step === 2 && <Step2 handleStep={handleStep} />}
            {step === 3 && <Step3 handleStep={handleStep} />}
            {step === 4 && (
              <Step4
                setValuesChanged={setValuesChanged}
                plants={plants}
                handleStep={handleStep}
              />
            )}
            {step === 5 && <FinalStep setValuesChanged={setValuesChanged} />}
          </form>
        </FormProvider>
      </div>
    </>
  );
}

export default ExistingDrugOrigin;
